import { useCallback, useEffect, useRef, useState } from "react";
import {
  FaChartBar,
  FaClipboardList,
  FaEnvelope,
  FaFileAlt,
  FaFolderOpen,
  FaIdCard,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRegEdit,
  FaSave,
  FaSearch,
  FaTimes,
  FaUserAlt,
} from "react-icons/fa";
import ActionPageLayout from "../layouts/ActionPageLayout";
import usePageTitle from "../hooks/usePageTitle";
import useTelecallingNotification from "../hooks/useTelecallingNotification";
import TelecallingNotificationAlert from "../components/TelecallingNotificationAlert";
import { useAuth } from "../context/AuthContext";
import {
  fetchTelecallingRecordsList,
  fetchTelecallingRecordsMetadata,
  fetchTelecallingRecordsSummary,
  saveTelecallingCallEntry,
  searchTelecallingRecord,
} from "../services/telecallingRecordsApi";
import { fetchApplicationByLeadId } from "../services/applicationApi";
import { pullCrifBureauReportCallCenter } from "../services/crifBureauApi";
import { getSavedAuthUser } from "../utils/authStorage";

const inputClass =
  "h-[38px] w-full rounded-md border border-[#d8dee6] bg-white px-3 text-base outline-none transition placeholder:text-[#4b5563] focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.18)]";

const modalInputClass =
  "h-[38px] w-full rounded-md border border-[#d8dee6] bg-white px-3 text-base outline-none transition placeholder:text-[#6b7280] focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.18)]";

const modalTextareaClass =
  "min-h-[92px] w-full rounded-md border border-[#d8dee6] bg-white px-3 py-2 text-base leading-[1.45] outline-none transition placeholder:text-[#6b7280] focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.18)]";

const modalFieldErrorClass =
  "border-[#dc3545] bg-[#fff8f8] focus:border-[#dc3545] focus:shadow-[0_0_0_4px_rgba(220,53,69,0.16)]";

const defaultTelecallingContactId = 1;
const defaultTelecallingAgentId = 10;

const filterButtons = [
  {
    id: "all",
    label: "All",
    apiStatus: "",
    className: "border-[#6c757d] bg-[#6c757d] text-white hover:bg-[#5c656d]",
  },
  {
    id: "appointment",
    label: "Appointment",
    apiStatus: "Appointment Created",
    className: "border-[#198754] bg-[#198754] text-white hover:bg-[#157347]",
  },
  {
    id: "called",
    label: "Called",
    apiStatus: "Connected-Completed",
    className: "border-[#dc3545] bg-white text-[#dc3545] hover:bg-[#fff5f5]",
  },
  {
    id: "followup",
    label: "Followup",
    apiStatus: "Connected-Followup Required",
    className: "border-[#0d73ff] bg-[#1773f8] text-white hover:bg-[#0d63dc]",
  },
  {
    id: "retry",
    label: "Retry",
    apiStatus: "Not Connected",
    className: "border-[#212529] bg-[#212529] text-white hover:bg-[#111417]",
  },
  {
    id: "yet-to-call",
    label: "Yet to Call",
    apiStatus: "Yet to call",
    className:
      "border-[#ffc107] bg-[#ffc107] text-[#111827] hover:bg-[#f0b400]",
  },
  {
    id: "eligibility",
    label: "Eligibility check list",
    apiStatus: "Eligibility Check",
    className:
      "border-[#06b6d4] bg-[#06b6d4] text-[#111827] hover:bg-[#0891b2]",
  },
];

const summaryFields = [
  ["Total Assigned", "total_assigned"],
  ["Called Today", "total_called"],
  ["Yet to Call", "total_left"],
  ["Total appointment taken", "total_appointment_taken"],
];

const tableHeaders = [
  "Phone",
  "Name",
  "City",
  "Status",
  "Sub-status",
  "Action",
];

const defaultRecordsPerPage = 10;

const emptyCallSummary = {
  total_assigned: 0,
  total_called: 0,
  total_left: 0,
  total_appointment_taken: 0,
};

const emptyCallListMeta = {
  total: 0,
  page: 1,
  limit: defaultRecordsPerPage,
  totalPages: 1,
};

const emptyTelecallingMetadata = {
  options: {
    callStatuses: [],
    callListStatuses: [],
    productOptions: [],
    statusSubStatusOptions: {},
    documentOptions: {
      doc_pan: [],
      address_type: [],
      doc_identity: [],
      doc_address: [],
      doc_income: [],
      doc_chq: [],
      coapp_identity: [],
      coapp_photo: [],
      doc_business: [],
    },
    stateOptions: [],
    fosOptions: [],
  },
  defaults: {
    callListPageSize: defaultRecordsPerPage,
    dailyAssignmentLimit: 0,
  },
};

function normalizeTelecallingMetadata(metadata) {
  const options = metadata?.options || {};
  const documentOptions = options.documentOptions || {};

  return {
    options: {
      ...emptyTelecallingMetadata.options,
      ...options,
      documentOptions: {
        ...emptyTelecallingMetadata.options.documentOptions,
        ...documentOptions,
      },
    },
    defaults: {
      ...emptyTelecallingMetadata.defaults,
      ...(metadata?.defaults || {}),
    },
  };
}

const searchedCustomer = {
  searchPhone: "1244567890",
  personal: {
    name: "Tes124",
    phone: "1244567890",
    city: "Others",
    house: "-",
    street: "-",
    locality: "-",
  },
  documents: {
    panPresent: "N/A",
    panNumber: "N/A",
    idProof: "N/A",
    dob: "N/A",
  },
  pil: {
    membershipNo: "PIL Info Not Fetched Yet",
    memberSince: "",
    classification: "",
  },
  communication: {
    campaignName: "",
    contactSource: "Existing Customer",
    sourceDesc: "N/A",
    lastCalled: "2026-05-28 19:24:17",
    status: "Appointment Created",
    subStatus: "Appointment Created for today",
    assignedTo: "Shavankumar | 9560605393",
    comments: "",
  },
  demographics: {
    name1: "-",
    name2: "-",
    dob1: "-",
    dob2: "-",
    pan1: "-",
    pan2: "-",
    uid1: "-",
    uid2: "-",
    ckyc1: "-",
    dl1: "-",
    passport1: "-",
    voterId1: "-",
    nrega1: "-",
    rationCard1: "-",
  },
  application: {
    loanProduct: "N/A",
    taskType: "N/A",
    taskStatus: "N/A",
    assignedTo: "N/A",
    appId: "N/A",
    appStatus: "N/A",
    appLastUpdate: "N/A",
  },
  eligibility: {
    amount: "-",
    tenure: "0 months",
    irrScore: "%",
    emi: "-",
    score: "-",
    message: "New_to_Credit",
  },
};

const demographicFields = [
  ["Name 1", "name1"],
  ["Name 2", "name2"],
  ["DOB 1", "dob1"],
  ["DOB 2", "dob2"],
  ["PAN 1", "pan1"],
  ["PAN 2", "pan2"],
  ["UID 1", "uid1"],
  ["UID 2", "uid2"],
  ["CKYC 1", "ckyc1"],
  ["DL 1", "dl1"],
  ["Passport 1", "passport1"],
  ["VOTERID 1", "voterId1"],
  ["NREGA 1", "nrega1"],
  ["Ration Card 1", "rationCard1"],
];

const addressRows = ["Address 1", "Address 2", "Address 3"];
const contactRows = ["Contact 1", "Contact 2", "Contact 3"];
const emailRows = ["Email 1", "Email 2", "Email 3"];

function defaultDemographicTables() {
  return {
    addresses: addressRows.map((address) => [address, "-", "-"]),
    contacts: contactRows.map((contact) => [contact, "-", "-"]),
    emails: emailRows.map((email) => [email, "-"]),
  };
}

function optionValue(option) {
  if (option && typeof option === "object") {
    return option.value ?? option.label ?? "";
  }

  return option ?? "";
}

function optionLabel(option) {
  if (option && typeof option === "object") {
    return option.label ?? option.value ?? "";
  }

  return option ?? "";
}

function normalizeOptions(options = []) {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.filter((option) => optionValue(option) !== "");
}

function getVisibleOptions(options, value) {
  const normalizedOptions = normalizeOptions(options);
  const optionValues = new Set(normalizedOptions.map(optionValue));

  if (value && !optionValues.has(value)) {
    return [{ value, label: value }, ...normalizedOptions];
  }

  return normalizedOptions;
}

function getDocumentOptions(metadata, key) {
  return metadata.options.documentOptions?.[key] || [];
}

function editableFieldValue(value) {
  if (!value || value === "N/A") {
    return "";
  }

  return value;
}

function displayValue(value, fallback = "-") {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : fallback;
  }

  return value;
}

function formatIrrPercent(value) {
  const valueText = String(displayValue(value, "")).trim();

  if (!valueText) {
    return "-";
  }

  if (valueText.endsWith("%")) {
    return valueText;
  }

  const numericValue = Number(valueText.replace(/,/g, ""));

  if (!Number.isFinite(numericValue)) {
    return valueText;
  }

  const percentValue =
    Math.abs(numericValue) <= 1 ? numericValue * 100 : numericValue;

  return `${Number(percentValue.toFixed(2))}%`;
}

function toggleSelectedOption(options, option) {
  if (options.includes(option)) {
    return options.filter((selectedOption) => selectedOption !== option);
  }

  return [...options, option];
}

function fieldOrEmpty(value) {
  return editableFieldValue(value === "-" ? "" : value);
}

function summaryCount(value, isLoading) {
  if (isLoading) {
    return "Loading...";
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString("en-IN")
    : "0";
}

function callListStatusForFilter(filterId) {
  return (
    filterButtons.find((filter) => filter.id === filterId)?.apiStatus || ""
  );
}

function callStatusMatchesFilter(status, filterId) {
  const filterStatus = callListStatusForFilter(filterId);

  if (!filterStatus) {
    return true;
  }

  return (
    String(status || "").trim().toLowerCase() ===
    filterStatus.trim().toLowerCase()
  );
}

function callListActionForStatus(status) {
  switch (String(status || "").trim().toLowerCase()) {
    case "appointment created":
      return { action: "Appointment Created", actionClass: "success" };
    case "connected-completed":
      return { action: "Called", actionClass: "danger" };
    case "connected-followup required":
      return { action: "Followup", actionClass: "primary" };
    case "not connected":
      return { action: "Retry", actionClass: "dark" };
    case "eligibility check":
      return { action: "Pending", actionClass: "warning" };
    case "yet to call":
      return { action: "Yet to Call", actionClass: "warning" };
    default:
      return { action: "Open", actionClass: "" };
  }
}

function normalizeCallListRecord(row) {
  return {
    phone: row.phone || row.action?.phone || "",
    name: row.name || "-",
    city: row.city || "-",
    status: row.call_status || row.status || "",
    subStatus: row.sub_status || "",
    action: row.action?.button_text || "Open",
    actionClass: row.action?.button_class || "",
    priority: row.customer_priority || "",
    calledToday: row.called_today,
    isUrgent: Boolean(row.is_urgent),
    isEligibility: Boolean(row.is_eligibility),
    eligibilityPending: Boolean(row.eligibility_pending),
    raw: row,
  };
}

function normalizeEligibilityProduct(product, index) {
  const eligibilityProduct = product || {};

  return {
    productName:
      eligibilityProduct.product_name ||
      eligibilityProduct.product ||
      eligibilityProduct.loan_product ||
      eligibilityProduct.name ||
      `Product ${index + 1}`,
    amount: displayValue(
      eligibilityProduct.amount || eligibilityProduct.eligible_amount,
    ),
    tenure: displayValue(
      eligibilityProduct.tenure || eligibilityProduct.tenure_months,
    ),
    irrScore: displayValue(
      formatIrrPercent(
        eligibilityProduct.irr_score ||
          eligibilityProduct.irrScore ||
          eligibilityProduct.irr,
      ),
    ),
    emi: displayValue(eligibilityProduct.emi),
    score: displayValue(
      eligibilityProduct.crif_score || eligibilityProduct.crifScore,
    ),
    message: displayValue(
      eligibilityProduct.message ||
        eligibilityProduct.reason ||
        eligibilityProduct.status,
      "",
    ),
  };
}

function normalizeBureauPayloadValue(value) {
  if (value === null || value === undefined || value === false) {
    return "";
  }

  if (Array.isArray(value)) {
    return value.map(normalizeBureauPayloadValue).filter(Boolean).join(" ");
  }

  if (typeof value === "object") {
    return firstBureauValue(value.value, value.label, value.name);
  }

  const trimmedValue = String(value).trim();

  if (
    !trimmedValue ||
    trimmedValue === "-" ||
    trimmedValue.toUpperCase() === "N/A"
  ) {
    return "";
  }

  return trimmedValue;
}

function firstBureauValue(...values) {
  for (const value of values) {
    const normalizedValue = normalizeBureauPayloadValue(value);

    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return "";
}

function sourceBureauValue(source, keys) {
  if (!source) {
    return "";
  }

  for (const key of keys) {
    const value = firstBureauValue(source[key]);

    if (value) {
      return value;
    }
  }

  return "";
}

function splitCustomerNameForBureau(name) {
  const cleanName = normalizeBureauPayloadValue(name).replace(
    /\b\d{10}\b/g,
    "",
  );
  const parts = cleanName.split(/\s+/).filter(Boolean);
  const salutationPattern = /^(mr|mrs|ms|miss|shri|smt)\.?$/i;

  if (parts.length > 1 && salutationPattern.test(parts[0])) {
    parts.shift();
  }

  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}

function normalizeBureauPayloadDate(value) {
  const rawValue = firstBureauValue(value);

  if (!rawValue) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
    return rawValue.slice(0, 10);
  }

  const [day, month, year] = rawValue.split(/[-/]/);

  if (day && month && year?.length === 4) {
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  return rawValue;
}

function compactBureauAddressParts(parts) {
  const seenParts = new Set();

  return parts
    .map(normalizeBureauPayloadValue)
    .filter((part) => {
      const normalizedPart = part.toUpperCase();

      if (!part || seenParts.has(normalizedPart)) {
        return false;
      }

      seenParts.add(normalizedPart);
      return true;
    })
    .join(" ");
}

function buildTelecallingBureauPayload(phone, searchData, fallbackRecord = {}) {
  const contact = searchData?.contact || {};
  const formData = searchData?.form_data || {};
  const rawRecord = fallbackRecord.raw || {};
  const customerName = firstBureauValue(
    sourceBureauValue(formData, ["customer_name", "name"]),
    sourceBureauValue(contact, ["customer_name", "name"]),
    sourceBureauValue(rawRecord, ["customer_name", "name"]),
    fallbackRecord.name,
  );
  const nameParts = splitCustomerNameForBureau(customerName);
  const address = firstBureauValue(
    sourceBureauValue(formData, [
      "address",
      "localityAddress",
      "locality_address",
    ]),
    sourceBureauValue(contact, [
      "address",
      "localityAddress",
      "locality_address",
    ]),
    sourceBureauValue(rawRecord, [
      "address",
      "localityAddress",
      "locality_address",
    ]),
    compactBureauAddressParts([
      sourceBureauValue(formData, ["house_building_number"]),
      sourceBureauValue(formData, ["street_name"]),
      sourceBureauValue(formData, ["locality_area"]),
      sourceBureauValue(formData, ["city"]),
      sourceBureauValue(formData, ["state"]),
      sourceBureauValue(formData, ["zip_code", "pincode", "pinCode"]),
      sourceBureauValue(contact, ["house_building_number"]),
      sourceBureauValue(contact, ["street_name"]),
      sourceBureauValue(contact, ["locality_area"]),
      sourceBureauValue(contact, ["city", "fd_city"]),
      sourceBureauValue(contact, ["state"]),
      sourceBureauValue(contact, ["zip_code", "pincode", "pinCode"]),
      sourceBureauValue(rawRecord, ["house_building_number"]),
      sourceBureauValue(rawRecord, ["street_name"]),
      sourceBureauValue(rawRecord, ["locality_area"]),
      sourceBureauValue(rawRecord, ["city", "fd_city"]),
      sourceBureauValue(rawRecord, ["state"]),
      sourceBureauValue(rawRecord, ["zip_code", "pincode", "pinCode"]),
    ]),
  );

  return {
    phone: firstBureauValue(
      phone,
      sourceBureauValue(formData, ["phone"]),
      sourceBureauValue(contact, ["phone"]),
      sourceBureauValue(rawRecord, ["phone"]),
    ),
    firstName: firstBureauValue(
      sourceBureauValue(formData, ["first_name", "firstName"]),
      sourceBureauValue(contact, ["first_name", "firstName"]),
      sourceBureauValue(rawRecord, ["first_name", "firstName"]),
      nameParts.firstName,
    ),
    lastName: firstBureauValue(
      sourceBureauValue(formData, ["last_name", "lastName"]),
      sourceBureauValue(contact, ["last_name", "lastName"]),
      sourceBureauValue(rawRecord, ["last_name", "lastName"]),
      nameParts.lastName,
    ),
    fatherName: firstBureauValue(
      sourceBureauValue(formData, ["father_name", "fatherName"]),
      sourceBureauValue(contact, ["father_name", "fatherName"]),
      sourceBureauValue(rawRecord, ["father_name", "fatherName"]),
    ),
    dob: normalizeBureauPayloadDate(
      firstBureauValue(
        sourceBureauValue(formData, ["dob_full", "dob"]),
        sourceBureauValue(contact, ["dob_full", "dob"]),
        sourceBureauValue(rawRecord, ["dob_full", "dob"]),
      ),
    ),
    pan: firstBureauValue(
      sourceBureauValue(formData, ["pan_number", "pan"]),
      sourceBureauValue(contact, ["pan_number", "pan"]),
      sourceBureauValue(rawRecord, ["pan_number", "pan"]),
    ).toUpperCase(),
    city: firstBureauValue(
      sourceBureauValue(formData, ["city", "fd_city"]),
      sourceBureauValue(contact, ["city", "fd_city"]),
      sourceBureauValue(rawRecord, ["city", "fd_city"]),
      fallbackRecord.city,
    ),
    state: firstBureauValue(
      sourceBureauValue(formData, ["state"]),
      sourceBureauValue(contact, ["state"]),
      sourceBureauValue(rawRecord, ["state"]),
    ),
    country: firstBureauValue(
      sourceBureauValue(formData, ["country"]),
      sourceBureauValue(contact, ["country"]),
      sourceBureauValue(rawRecord, ["country"]),
      "INDIA",
    ),
    pinCode: firstBureauValue(
      sourceBureauValue(formData, ["zip_code", "pincode", "pinCode"]),
      sourceBureauValue(contact, ["zip_code", "pincode", "pinCode"]),
      sourceBureauValue(rawRecord, ["zip_code", "pincode", "pinCode"]),
    ),
    address,
    productInterested: firstBureauValue(
      sourceBureauValue(formData, [
        "loan_product",
        "product_interested",
        "productInterested",
      ]),
      sourceBureauValue(contact, [
        "Loan_Product",
        "loan_product",
        "product_interested",
        "productInterested",
      ]),
      sourceBureauValue(rawRecord, [
        "Loan_Product",
        "loan_product",
        "product_interested",
        "productInterested",
      ]),
      fallbackRecord.loanProduct,
    ),
  };
}

function hasBureauPayloadDetails(payload) {
  return Boolean(
    firstBureauValue(
      payload?.firstName,
      payload?.lastName,
      payload?.fatherName,
      payload?.dob,
      payload?.pan,
      payload?.city,
      payload?.state,
      payload?.pinCode,
      payload?.address,
      payload?.productInterested,
    ),
  );
}

function shouldRetryBureauPull(initialPayload, enrichedPayload) {
  if (!hasBureauPayloadDetails(enrichedPayload)) {
    return false;
  }

  return Object.keys(enrichedPayload).some(
    (key) =>
      firstBureauValue(enrichedPayload[key]) !==
      firstBureauValue(initialPayload[key]),
  );
}

function extractBureauDemogVariations(bureauResult) {
  const data = bureauResult?.data ?? bureauResult;
  const dataItems = Array.isArray(data) ? data : [data];

  return dataItems.flatMap((item) => {
    const nestedData = item?.data || {};
    const report = item?.report || {};
    const variations = [
      item?.demogVariations,
      item?.demog_variations,
      item?.demographicVariations,
      item?.demographic_variations,
      nestedData?.demogVariations,
      nestedData?.demog_variations,
      report?.demogVariations,
      report?.demog_variations,
    ];

    return variations.flatMap((variationGroup) =>
      Array.isArray(variationGroup) ? variationGroup : [],
    );
  });
}

function bureauVariationValue(variation) {
  return firstBureauValue(
    variation?.value_text,
    variation?.valueText,
    variation?.value,
  );
}

function bureauVariationReportedDate(variation) {
  return firstBureauValue(
    variation?.reported_dt,
    variation?.reportedDate,
    variation?.reported_on,
    variation?.reportedOn,
    variation?.first_reported_dt,
    variation?.created_at,
  );
}

function bureauVariationTimestamp(variation) {
  const rawDate = bureauVariationReportedDate(variation);
  const timestamp = Date.parse(rawDate);

  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function bureauVariationCategory(variation) {
  const variationType = String(
    variation?.variation_type || variation?.type || "",
  )
    .toUpperCase()
    .replace(/\s+/g, "-");

  if (variationType.includes("ADDRESS")) {
    return "address";
  }

  if (variationType.includes("EMAIL")) {
    return "email";
  }

  if (
    variationType.includes("PHONE") ||
    variationType.includes("MOBILE") ||
    variationType.includes("CONTACT")
  ) {
    return "contact";
  }

  if (variationType.includes("VOTER")) {
    return "voterId";
  }

  if (variationType.includes("RATION")) {
    return "rationCard";
  }

  if (variationType.includes("PASSPORT")) {
    return "passport";
  }

  if (variationType.includes("NREGA")) {
    return "nrega";
  }

  if (variationType.includes("CKYC")) {
    return "ckyc";
  }

  if (
    variationType.includes("DRIV") ||
    variationType.includes("DL-VARIATIONS")
  ) {
    return "dl";
  }

  if (
    variationType.includes("UID") ||
    variationType.includes("AADHAAR") ||
    variationType.includes("AADHAR")
  ) {
    return "uid";
  }

  if (variationType.includes("PAN")) {
    return "pan";
  }

  if (variationType.includes("DOB") || variationType.includes("BIRTH")) {
    return "dob";
  }

  if (variationType.includes("NAME")) {
    return "name";
  }

  return "";
}

function latestBureauVariations(variations, category, limit) {
  const seenValues = new Set();

  return variations
    .filter((variation) => bureauVariationCategory(variation) === category)
    .filter((variation) => bureauVariationValue(variation))
    .sort((left, right) => {
      const timestampDiff =
        bureauVariationTimestamp(right) - bureauVariationTimestamp(left);

      if (timestampDiff) {
        return timestampDiff;
      }

      return Number(right?.id || 0) - Number(left?.id || 0);
    })
    .filter((variation) => {
      const normalizedValue = bureauVariationValue(variation).toUpperCase();

      if (seenValues.has(normalizedValue)) {
        return false;
      }

      seenValues.add(normalizedValue);
      return true;
    })
    .slice(0, limit);
}

function formatBureauReportedDate(value) {
  const rawValue = firstBureauValue(value);

  if (!rawValue) {
    return "-";
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
    return rawValue.slice(0, 10);
  }

  const parsedDate = new Date(rawValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return rawValue;
  }

  return parsedDate.toISOString().slice(0, 10);
}

function buildBureauVariationRows(
  variations,
  category,
  labels,
  includeReportedDate,
) {
  const latestRows = latestBureauVariations(
    variations,
    category,
    labels.length,
  );

  return labels.map((label, index) => {
    const variation = latestRows[index];

    if (!variation) {
      return includeReportedDate ? [label, "-", "-"] : [label, "-"];
    }

    if (includeReportedDate) {
      return [
        label,
        formatBureauReportedDate(bureauVariationReportedDate(variation)),
        bureauVariationValue(variation),
      ];
    }

    return [label, bureauVariationValue(variation)];
  });
}

function mapBureauDemographics(bureauResult) {
  const variations = extractBureauDemogVariations(bureauResult);
  const hasVariations = variations.some((variation) =>
    bureauVariationValue(variation),
  );
  const demographics = {};
  const fieldGroups = [
    ["name", ["name1", "name2"]],
    ["dob", ["dob1", "dob2"]],
    ["pan", ["pan1", "pan2"]],
    ["uid", ["uid1", "uid2"]],
    ["ckyc", ["ckyc1"]],
    ["dl", ["dl1"]],
    ["passport", ["passport1"]],
    ["voterId", ["voterId1"]],
    ["nrega", ["nrega1"]],
    ["rationCard", ["rationCard1"]],
  ];

  fieldGroups.forEach(([category, keys]) => {
    latestBureauVariations(variations, category, keys.length).forEach(
      (variation, index) => {
        demographics[keys[index]] = bureauVariationValue(variation);
      },
    );
  });

  return {
    hasVariations,
    demographics,
    demographicTables: hasVariations
      ? {
          addresses: buildBureauVariationRows(
            variations,
            "address",
            addressRows,
            true,
          ),
          contacts: buildBureauVariationRows(
            variations,
            "contact",
            contactRows,
            true,
          ),
          emails: buildBureauVariationRows(
            variations,
            "email",
            emailRows,
            false,
          ),
        }
      : defaultDemographicTables(),
  };
}

function mergeBureauDemographics(customer, bureauResult) {
  const bureauDemographics = mapBureauDemographics(bureauResult);

  if (!bureauDemographics.hasVariations) {
    return customer;
  }

  return {
    ...customer,
    demographics: {
      ...customer.demographics,
      ...bureauDemographics.demographics,
    },
    demographicTables: bureauDemographics.demographicTables,
  };
}

function apiObject(source, camelKey, snakeKey) {
  const value = source?.[camelKey] || source?.[snakeKey];

  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
}

function formatApiLabel(value) {
  const rawValue = firstBureauValue(value);

  if (!rawValue) {
    return "";
  }

  return rawValue
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
}

function formatApplicationDateTime(value) {
  const rawValue = firstBureauValue(value);

  if (!rawValue) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(rawValue)) {
    return rawValue
      .replace("T", " ")
      .replace(/\.\d+Z?$/, "")
      .replace(/Z$/, "")
      .slice(0, 19);
  }

  return rawValue;
}

function getApplicationLookup(searchData) {
  const lookup =
    searchData?.applicationLookup || searchData?.application_lookup;
  const summary =
    lookup?.summary ||
    searchData?.applicationSummary ||
    searchData?.application_summary ||
    {};
  const application =
    lookup?.application ||
    searchData?.applicationDetails ||
    searchData?.application_details ||
    {};
  const applicationData = {
    ...summary,
    ...application,
  };

  if (
    !firstBureauValue(
      applicationData.id,
      applicationData.applicationId,
      applicationData.application_id,
      applicationData.leadId,
      applicationData.lead_id,
      applicationData.productType,
      applicationData.product_type,
    )
  ) {
    return null;
  }

  return {
    application: applicationData,
    applicationId:
      lookup?.applicationId ||
      applicationData.id ||
      applicationData.applicationId ||
      applicationData.application_id,
    summary,
  };
}

function mergeApplicationCustomerData(customer, searchData) {
  const lookup = getApplicationLookup(searchData);

  if (!lookup) {
    return customer;
  }

  const application = lookup.application;
  const loanApprovalDetails = apiObject(
    application,
    "loanApprovalDetails",
    "loan_approval_details",
  );
  const productName = formatApiLabel(
    firstBureauValue(application.productType, application.product_type),
  );
  const applicationStatus = formatApiLabel(
    firstBureauValue(application.state, application.status),
  );
  const applicationId = firstBureauValue(
    lookup.applicationId,
    application.id,
    application.applicationId,
    application.application_id,
  );
  const lastUpdate = formatApplicationDateTime(
    firstBureauValue(
      application.updatedAt,
      application.updated_at,
      loanApprovalDetails.softApprovalDate,
      loanApprovalDetails.soft_approval_date,
    ),
  );

  return {
    ...customer,
    applicationLookupError: searchData?.applicationLookupError || "",
    application: {
      ...customer.application,
      loanProduct: displayValue(productName, customer.application.loanProduct),
      taskType: displayValue(
        formatApiLabel(application.taskType || application.task_type),
        customer.application.taskType,
      ),
      taskStatus: displayValue(
        formatApiLabel(
          application.taskStatus ||
            application.task_status ||
            application.state ||
            application.status,
        ),
        customer.application.taskStatus,
      ),
      assignedTo: displayValue(
        firstBureauValue(
          application.assignedTo,
          application.assigned_to,
          loanApprovalDetails.approvedBy,
          loanApprovalDetails.approved_by,
          application.createdBy,
          application.created_by,
        ),
        customer.application.assignedTo,
      ),
      appId: displayValue(applicationId, customer.application.appId),
      appStatus: displayValue(
        applicationStatus,
        customer.application.appStatus,
      ),
      appLastUpdate: displayValue(
        lastUpdate,
        customer.application.appLastUpdate,
      ),
    },
  };
}

function mapSearchResponseToCustomer(searchData, fallbackPhone = "") {
  const contact = searchData?.contact || {};
  const formData = searchData?.form_data || {};
  const eligibilityProducts = Array.isArray(searchData?.eligibility_products)
    ? searchData.eligibility_products.map(normalizeEligibilityProduct)
    : [];
  const phone =
    formData.phone ||
    contact.phone ||
    searchData?.searched_phone ||
    fallbackPhone;
  const name =
    formData.customer_name ||
    formData.name ||
    contact.customer_name ||
    contact.name;
  const city =
    formData.city ||
    contact.fd_city ||
    contact.city ||
    searchedCustomer.personal.city;

  const customer = {
    ...searchedCustomer,
    contactId: contact.id,
    agentId: formData.agent_id || contact.agent_id || searchData?.agent_id,
    agentName:
      formData.agent_name || contact.agent_name || searchData?.agent_name,
    searchPhone: phone,
    formDefaults: {
      productInterested: formData.loan_product || contact.Loan_Product || "",
      appointmentDate: apiDateTimeToInputDateTime(
        formData.appointment_date || contact.appointment_date,
      ),
      followupDate: apiDateTimeToInputDateTime(
        formData.followup_date || contact.followup_date,
      ),
      state: formData.state || contact.state || "",
      zipCode: formData.zip_code || contact.zip_code || "",
      docPan: formData.doc_pan || contact.doc_pan || "",
      panNumber: formData.pan_number || contact.pan_number || "",
      dobFull: apiDateToInputDate(formData.dob_full || contact.dob_full),
      fatherName: formData.father_name || contact.father_name || "",
      addressType: formData.address_type || contact.address_type || "",
      docIdentity: normalizeSelectionArray(
        formData.doc_identity || contact.doc_identity,
      ),
      docAddress: normalizeSelectionArray(
        formData.doc_address || contact.doc_address,
      ),
      docIncome: normalizeSelectionArray(
        formData.doc_income || contact.doc_income,
      ),
      docChq: formData.doc_chq || contact.doc_chq || "",
      coappIdentity: normalizeSelectionArray(
        formData.coapp_identity || contact.coapp_identity,
      ),
      coappPhoto: formData.coapp_photo || contact.coapp_photo || "",
      docBusiness: normalizeSelectionArray(
        formData.doc_business || contact.doc_business,
      ),
    },
    personal: {
      ...searchedCustomer.personal,
      name: displayValue(name),
      phone: displayValue(phone),
      city: displayValue(city),
      house: displayValue(
        formData.house_building_number || contact.house_building_number,
      ),
      street: displayValue(formData.street_name || contact.street_name),
      locality: displayValue(formData.locality_area || contact.locality_area),
    },
    documents: {
      ...searchedCustomer.documents,
      panPresent: displayValue(formData.doc_pan || contact.doc_pan, "N/A"),
      panNumber: displayValue(formData.pan_number || contact.pan_number, "N/A"),
      idProof: displayValue(
        normalizeSelectionArray(formData.doc_identity || contact.doc_identity),
        "N/A",
      ),
      dob: displayValue(formData.dob_full || contact.dob_full, "N/A"),
    },
    pil: {
      membershipNo:
        formData.membership_number || searchedCustomer.pil.membershipNo,
      memberSince: displayValue(formData.member_since_display, ""),
      classification: displayValue(formData.contractor_classification, ""),
    },
    communication: {
      ...searchedCustomer.communication,
      campaignName: displayValue(
        formData.campaign_name || contact.campaign_name,
        "",
      ),
      contactSource: displayValue(
        formData.contact_source || contact.contact_source,
        "",
      ),
      sourceDesc: displayValue(
        formData.contact_source_description ||
          contact.contact_source_description,
        "N/A",
      ),
      lastCalled: displayValue(
        formData.last_call_date || contact.last_call_date,
        "-",
      ),
      status: displayValue(formData.call_status || contact.call_status, "N/A"),
      subStatus: displayValue(formData.sub_status || contact.sub_status, "N/A"),
      assignedTo: displayValue(formData.fos_name || contact.fos_name, ""),
      comments: displayValue(
        formData.comments || contact.comments || formData.wa_chat_log,
        "",
      ),
    },
    application: {
      ...searchedCustomer.application,
      loanProduct: displayValue(
        formData.loan_product || contact.Loan_Product,
        "N/A",
      ),
      taskType: displayValue(formData.task_type || contact.task_type, "N/A"),
      taskStatus: displayValue(
        formData.task_status || contact.task_status,
        "N/A",
      ),
      assignedTo: displayValue(
        formData.task_assigned_to || contact.task_assigned_to,
        "N/A",
      ),
      appId: displayValue(
        formData.applicationId || contact.applicationId,
        "N/A",
      ),
      appStatus: displayValue(
        formData.application_status || contact.application_status,
        "N/A",
      ),
      appLastUpdate: displayValue(
        formData.app_last_update || contact.App_Last_Update,
        "N/A",
      ),
    },
    eligibilityProducts,
    eligibility: eligibilityProducts[0] || {
      amount: "-",
      tenure: "-",
      irrScore: "-",
      emi: "-",
      score: "-",
      message: "No eligibility products available",
    },
  };

  return mergeApplicationCustomerData(customer, searchData);
}

function inputDateToApiDate(value) {
  const [year, month, day] = String(value || "").split("-");

  if (!day || !month || !year) {
    return "";
  }

  return `${day}-${month}-${year}`;
}

function phpEmpty(value) {
  if (value === null || value === undefined || value === false) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return String(value).trim() === "" || String(value) === "0";
}

function normalizeArray(value) {
  if (Array.isArray(value)) {
    return value.filter((item) => !phpEmpty(item));
  }

  return phpEmpty(value) ? [] : [value];
}

function normalizeSelectionArray(value) {
  if (Array.isArray(value)) {
    return value.filter((item) => !phpEmpty(item));
  }

  if (phpEmpty(value)) {
    return [];
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();

    try {
      const parsedValue = JSON.parse(trimmedValue);

      if (Array.isArray(parsedValue)) {
        return parsedValue.filter((item) => !phpEmpty(item));
      }
    } catch {
      // Plain comma-separated strings are handled below.
    }

    return trimmedValue
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [value];
}

function apiDateTimeToInputDateTime(value) {
  if (phpEmpty(value)) {
    return "";
  }

  return String(value).replace(" ", "T").slice(0, 16);
}

function apiDateToInputDate(value) {
  const rawValue = String(value || "");

  if (!rawValue) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
    return rawValue.slice(0, 10);
  }

  const [day, month, year] = rawValue.split("-");

  if (!day || !month || !year) {
    return "";
  }

  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

function currentLocalDateTime() {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");

  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
}

function fieldClassName(baseClassName, error) {
  return error ? `${baseClassName} ${modalFieldErrorClass}` : baseClassName;
}

function validateCallEntryPayload(payload) {
  const errors = {};
  const documents = payload.documents || {};

  if (payload.call_status === "Appointment Created") {
    if (phpEmpty(payload.product_interested)) {
      errors.product_interested = "Product Interested is required.";
    }

    if (phpEmpty(documents.doc_pan)) {
      errors.doc_pan = "PAN Card selection is required.";
    }

    if (phpEmpty(documents.address_type)) {
      errors.address_type = "Current address type is required.";
    }

    if (normalizeArray(documents.doc_identity).length === 0) {
      errors.doc_identity = "Select at least one identity proof.";
    }

    if (normalizeArray(documents.doc_address).length === 0) {
      errors.doc_address = "Select at least one address proof.";
    }

    if (normalizeArray(documents.doc_income).length === 0) {
      errors.doc_income = "Select at least one income proof.";
    }

    if (phpEmpty(documents.doc_chq)) {
      errors.doc_chq = "CHQ Book selection is required.";
    }

    if (phpEmpty(payload.city)) {
      errors.city = "City is required.";
    }

    if (phpEmpty(payload.house_building_number)) {
      errors.house_building_number = "House/Building No is required.";
    }

    if (phpEmpty(payload.street_name)) {
      errors.street_name = "Street Name is required.";
    }

    if (phpEmpty(payload.locality_area)) {
      errors.locality_area = "Locality/Area is required.";
    }

    if (phpEmpty(payload.state)) {
      errors.state = "State is required.";
    }

    if (phpEmpty(payload.zip_code)) {
      errors.zip_code = "PIN/Zip code is required.";
    }
  }

  if (
    payload.call_status === "Connected-Followup Required" &&
    phpEmpty(payload.followup_date)
  ) {
    errors.followup_date = "Follow-up date is required.";
  }

  return errors;
}

function callEntrySuccessMessage(response) {
  const details = [
    response.form_data_id ? `Form Data ID: ${response.form_data_id}` : "",
    response.call_log_id ? `Call Log ID: ${response.call_log_id}` : "",
    response.document_status_id
      ? `Document Status ID: ${response.document_status_id}`
      : "",
  ].filter(Boolean);

  return [
    response.message || "Call entry saved successfully.",
    ...details,
  ].join(" ");
}

function SectionTitle({ icon, title, className = "text-[#0d73ff]" }) {
  return (
    <h3
      className={`mb-4 flex items-center gap-2 text-base font-semibold ${className}`}
    >
      {icon}
      {title}
    </h3>
  );
}

function DetailField({ label, value, className = "" }) {
  return (
    <div className={className}>
      <dt className="mb-1 text-base font-bold">{label}:</dt>
      <dd className="m-0 whitespace-pre-line break-words text-base leading-[1.45]">
        {value || "-"}
      </dd>
    </div>
  );
}

function DetailCard({ children }) {
  return (
    <section className="rounded-[0.5rem] border border-[#d1d5db] bg-white px-4 py-5">
      {children}
    </section>
  );
}

function MiniTable({ columns, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[21rem] border-collapse text-left text-base">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                className="border border-[#d9dfe8] bg-[#f8f9fa] px-2 py-2 font-bold"
                key={column}
                scope="col"
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]}>
              {row.map((cell, index) => (
                <td
                  className="border border-[#d9dfe8] px-2 py-2"
                  key={`${row[0]}-${index}`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FormField({
  label,
  required = false,
  children,
  className = "",
  error = "",
}) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-base font-bold">
        {label}
        {required ? " *" : ""}
      </span>
      {children}
      {error ? (
        <span className="mt-1.5 block text-sm font-medium text-[#b42318]">
          {error}
        </span>
      ) : null}
    </label>
  );
}

function ModalSelectField({
  label,
  required = false,
  value = "",
  options = [],
  placeholder,
  disabled = false,
  error = "",
  onChange,
}) {
  const visibleOptions = getVisibleOptions(options, value);

  return (
    <FormField error={error} label={label} required={required}>
      <select
        aria-invalid={error ? "true" : undefined}
        className={fieldClassName(modalInputClass, error)}
        disabled={disabled}
        value={value}
        onChange={onChange}
      >
        <option value="">{placeholder || `-- Select ${label} --`}</option>
        {visibleOptions.map((option) => {
          const selectValue = optionValue(option);
          const selectLabel = optionLabel(option);

          return (
            <option key={`${selectValue}-${selectLabel}`} value={selectValue}>
              {selectLabel}
            </option>
          );
        })}
      </select>
    </FormField>
  );
}

function OptionMessage({ isLoading }) {
  return (
    <p className="m-0 text-sm text-[#6b7280]">
      {isLoading ? "Loading options..." : "No options available."}
    </p>
  );
}

function CheckboxOption({ checked, label, onChange, value }) {
  const checkedProps = checked === undefined ? {} : { checked };
  const changeProps = onChange ? { onChange } : {};

  return (
    <label className="inline-flex items-center gap-2 text-base font-normal">
      <input
        className="h-4 w-4 rounded border-[#d8dee6] accent-[#0d73ff]"
        type="checkbox"
        value={value || label}
        {...checkedProps}
        {...changeProps}
      />
      {label}
    </label>
  );
}

function RadioOption({ checked, label, name, onChange, value }) {
  const checkedProps = checked === undefined ? {} : { checked };
  const changeProps = onChange ? { onChange } : {};

  return (
    <label className="inline-flex items-center gap-2 text-base font-normal">
      <input
        className="h-4 w-4 border-[#d8dee6] accent-[#0d73ff]"
        name={name}
        type="radio"
        value={value || label}
        {...checkedProps}
        {...changeProps}
      />
      {label}
    </label>
  );
}

function CheckboxOptions({ isLoading, onChange, options, selectedValues }) {
  const visibleOptions = normalizeOptions(options);

  if (!visibleOptions.length) {
    return <OptionMessage isLoading={isLoading} />;
  }

  return (
    <>
      {visibleOptions.map((option) => {
        const value = optionValue(option);
        const label = optionLabel(option);

        return (
          <CheckboxOption
            checked={
              selectedValues === undefined
                ? undefined
                : selectedValues.includes(value)
            }
            key={`${value}-${label}`}
            label={label}
            value={value}
            onChange={onChange ? () => onChange(value) : undefined}
          />
        );
      })}
    </>
  );
}

function RadioOptions({ isLoading, name, onChange, options, selectedValue }) {
  const visibleOptions = normalizeOptions(options);

  if (!visibleOptions.length) {
    return <OptionMessage isLoading={isLoading} />;
  }

  return (
    <>
      {visibleOptions.map((option) => {
        const value = optionValue(option);
        const label = optionLabel(option);

        return (
          <RadioOption
            checked={
              selectedValue === undefined ? undefined : selectedValue === value
            }
            key={`${value}-${label}`}
            label={label}
            name={name}
            value={value}
            onChange={onChange ? () => onChange(value) : undefined}
          />
        );
      })}
    </>
  );
}

function OptionGroup({ label, required = false, children, error = "" }) {
  return (
    <fieldset
      aria-invalid={error ? "true" : undefined}
      className={
        error ? "rounded-md border border-[#dc3545] bg-[#fff8f8] px-3 py-2" : ""
      }
    >
      <legend className="mb-2 text-base font-bold">
        {label}
        {required ? " *" : ""}
      </legend>
      <div className="flex flex-wrap gap-x-7 gap-y-2">{children}</div>
      {error ? (
        <p className="mt-2 text-sm font-medium text-[#b42318]">{error}</p>
      ) : null}
    </fieldset>
  );
}

function actionButtonClass(action, buttonClass = "") {
  if (buttonClass === "success") {
    return "bg-[#198754] text-white hover:bg-[#157347]";
  }

  if (buttonClass === "danger") {
    return "bg-[#dc3545] text-white hover:bg-[#bb2d3b]";
  }

  if (buttonClass === "primary") {
    return "bg-[#1773f8] text-white hover:bg-[#0d63dc]";
  }

  if (buttonClass === "dark") {
    return "bg-[#212529] text-white hover:bg-[#111417]";
  }

  if (buttonClass === "warning") {
    return "bg-[#ffc107] text-[#111827] hover:bg-[#f0b400]";
  }

  if (action === "Retry") {
    return "bg-[#212529] text-white hover:bg-[#111417]";
  }

  if (action === "Followup") {
    return "bg-[#1773f8] text-white hover:bg-[#0d63dc]";
  }

  if (action === "Called") {
    return "bg-[#dc3545] text-white hover:bg-[#bb2d3b]";
  }

  if (action === "Appointment Created") {
    return "bg-[#198754] text-white hover:bg-[#157347]";
  }

  return "bg-[#ffc107] text-[#111827] hover:bg-[#f0b400]";
}

function SearchResultDetails({ customer, onOpenCallEntry }) {
  const {
    personal,
    documents,
    pil,
    communication,
    demographics,
    application,
    eligibility,
  } = customer;
  const eligibilityProducts = customer.eligibilityProducts || [];
  const demographicTables =
    customer.demographicTables || defaultDemographicTables();

  return (
    <section className="mt-6">
      <div className="grid grid-cols-4 gap-4 max-[1400px]:grid-cols-2 max-[900px]:grid-cols-1">
        <DetailCard>
          <SectionTitle
            icon={
              <FaUserAlt
                className="h-4 w-4 flex-none text-[#c98655]"
                aria-hidden="true"
              />
            }
            title="Personal Info"
          />

          <dl className="grid grid-cols-3 gap-x-5 gap-y-4 max-[720px]:grid-cols-1">
            <DetailField label="Name" value={personal.name} />
            <DetailField label="Phone" value={personal.phone} />
            <DetailField label="City" value={personal.city} />
            <DetailField
              className="col-span-3 max-[720px]:col-span-1"
              label="House"
              value={personal.house}
            />
            <DetailField
              className="col-span-3 max-[720px]:col-span-1"
              label="Street"
              value={personal.street}
            />
            <DetailField
              className="col-span-3 max-[720px]:col-span-1"
              label="Locality"
              value={personal.locality}
            />
          </dl>

          <div className="my-5 h-px bg-[#e5e7eb]" />

          <SectionTitle
            className="text-[#ffb000]"
            icon={
              <FaFolderOpen className="h-4 w-4 flex-none" aria-hidden="true" />
            }
            title="Document Info"
          />
          <dl className="grid grid-cols-2 gap-x-7 gap-y-4 max-[720px]:grid-cols-1">
            <DetailField label="PAN Present" value={documents.panPresent} />
            <DetailField label="PAN Number" value={documents.panNumber} />
            <DetailField label="ID Proof" value={documents.idProof} />
            <DetailField label="DOB" value={documents.dob} />
          </dl>

          <div className="my-5 h-px bg-[#e5e7eb]" />

          <SectionTitle
            className="text-[#ffb000]"
            icon={
              <FaFileAlt
                className="h-4 w-4 flex-none text-[#8d819f]"
                aria-hidden="true"
              />
            }
            title="PIL Info"
          />
          <dl className="grid gap-2">
            <DetailField label="Membership No" value={pil.membershipNo} />
            <DetailField label="Member Since" value={pil.memberSince} />
            <DetailField
              label="Member Classification"
              value={pil.classification}
            />
          </dl>
        </DetailCard>

        <DetailCard>
          <SectionTitle
            className="text-[#16834a]"
            icon={
              <FaPhoneAlt
                className="h-4 w-4 flex-none rotate-[-18deg] text-[#d63384]"
                aria-hidden="true"
              />
            }
            title="Communication (Unknown)"
          />
          <dl className="grid grid-cols-2 gap-x-7 gap-y-4 max-[720px]:grid-cols-1">
            <DetailField
              className="col-span-2 max-[720px]:col-span-1"
              label="Campaign Name"
              value={communication.campaignName}
            />
            <DetailField
              label="Contact Source"
              value={communication.contactSource}
            />
            <DetailField label="Source Desc" value={communication.sourceDesc} />
            <DetailField label="Last Called" value={communication.lastCalled} />
            <DetailField label="Status" value={communication.status} />
            <DetailField label="Sub-Status" value={communication.subStatus} />
            <DetailField label="Assigned To" value={communication.assignedTo} />
            <DetailField
              className="col-span-2 max-[720px]:col-span-1"
              label="Comments"
              value={communication.comments}
            />
          </dl>
        </DetailCard>

        <DetailCard>
          <SectionTitle
            className="text-[#ffb000]"
            icon={
              <FaFileAlt
                className="h-4 w-4 flex-none text-[#8d819f]"
                aria-hidden="true"
              />
            }
            title="Demographic Info"
          />
          <dl className="mb-6 grid grid-cols-2 gap-x-7 gap-y-4 max-[720px]:grid-cols-1">
            {demographicFields.map(([label, key]) => (
              <DetailField key={key} label={label} value={demographics[key]} />
            ))}
          </dl>

          <SectionTitle
            className="text-[#111827]"
            icon={
              <FaMapMarkerAlt
                className="h-4 w-4 flex-none text-[#d63384]"
                aria-hidden="true"
              />
            }
            title="Addresses"
          />
          <MiniTable
            columns={["Type", "Reported On", "Value"]}
            rows={demographicTables.addresses}
          />

          <div className="mt-6">
            <SectionTitle
              className="text-[#111827]"
              icon={
                <FaPhoneAlt
                  className="h-4 w-4 flex-none rotate-[-18deg] text-[#d63384]"
                  aria-hidden="true"
                />
              }
              title="Contacts"
            />
            <MiniTable
              columns={["Type", "Reported On", "Value"]}
              rows={demographicTables.contacts}
            />
          </div>

          <div className="mt-6">
            <SectionTitle
              className="text-[#111827]"
              icon={
                <FaEnvelope
                  className="h-4 w-4 flex-none text-[#6d4b8a]"
                  aria-hidden="true"
                />
              }
              title="Emails"
            />
            <MiniTable
              columns={["Type", "Value"]}
              rows={demographicTables.emails}
            />
          </div>
        </DetailCard>

        <DetailCard>
          <SectionTitle
            className="text-[#dc3545]"
            icon={
              <FaClipboardList
                className="h-4 w-4 flex-none text-[#d58b63]"
                aria-hidden="true"
              />
            }
            title="Application Info"
          />
          <dl className="grid grid-cols-3 gap-x-7 gap-y-4 max-[720px]:grid-cols-1">
            <DetailField
              className="col-span-3 max-[720px]:col-span-1"
              label="Loan Product"
              value={application.loanProduct}
            />
            <DetailField label="Task Type" value={application.taskType} />
            <DetailField label="Task Status" value={application.taskStatus} />
            <DetailField label="Assigned To" value={application.assignedTo} />
          </dl>

          <div className="my-5 h-px bg-[#e5e7eb]" />

          <dl className="grid grid-cols-2 gap-x-7 gap-y-4 max-[720px]:grid-cols-1">
            <DetailField label="App ID" value={application.appId} />
            <DetailField label="App Status" value={application.appStatus} />
            <DetailField
              label="App Last Update"
              value={application.appLastUpdate}
            />
          </dl>

          <div className="my-5 h-px bg-[#e5e7eb]" />

          <SectionTitle
            className="text-[#ffb000]"
            icon={
              <FaIdCard
                className="h-4 w-4 flex-none text-[#8d819f]"
                aria-hidden="true"
              />
            }
            title="Eligibility Info"
          />
          {(eligibilityProducts.length
            ? eligibilityProducts
            : [eligibility]
          ).map((product, index) => (
            <div
              className={index > 0 ? "mt-5" : ""}
              key={product.productName || index}
            >
              <h4 className="mb-3 text-base font-bold text-[#0d73ff]">
                {product.productName || "Eligibility"}:
              </h4>
              <MiniTable
                columns={["Amount", "Tenure", "IRR", "EMI", "Score"]}
                rows={[
                  [
                    product.amount,
                    product.tenure,
                    product.irrScore,
                    product.emi,
                    product.score,
                  ],
                ]}
              />
              {product.message ? (
                <p className="mt-4 text-base">
                  <span className="font-bold">Message:</span> {product.message}
                </p>
              ) : null}
            </div>
          ))}
        </DetailCard>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          className="inline-flex h-[38px] items-center justify-center gap-2 rounded-md border border-[#0d73ff] bg-white px-5 text-base font-medium text-[#0d73ff] no-underline transition hover:bg-[#eff6ff] focus-visible:bg-[#eff6ff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9fc8ff]"
          type="button"
          onClick={onOpenCallEntry}
        >
          <FaRegEdit
            className="h-4 w-4 flex-none text-[#d58b63]"
            aria-hidden="true"
          />
          Open Call Entry Form
        </button>
      </div>
    </section>
  );
}

function CallEntryModal({
  customer,
  isMetadataLoading,
  isVisible,
  metadata,
  onClose,
  onSaved,
}) {
  const { communication, personal } = customer;
  const formDefaults = customer.formDefaults || {};
  const metadataOptions = metadata.options;
  const statusSubStatusOptions = metadataOptions.statusSubStatusOptions || {};
  const callEntryFormId = `call-entry-form-${customer.searchPhone || "customer"}`;
  const [panSelection, setPanSelection] = useState(formDefaults.docPan || "");
  const [customerName, setCustomerName] = useState(fieldOrEmpty(personal.name));
  const [productInterested, setProductInterested] = useState(
    formDefaults.productInterested || "",
  );
  const [status, setStatus] = useState(
    editableFieldValue(communication.status),
  );
  const [subStatus, setSubStatus] = useState(
    editableFieldValue(communication.subStatus),
  );
  const [appointmentDate, setAppointmentDate] = useState(
    formDefaults.appointmentDate || "",
  );
  const [followupDate, setFollowupDate] = useState(
    formDefaults.followupDate || "",
  );
  const [fosName, setFosName] = useState(
    editableFieldValue(communication.assignedTo),
  );
  const [city, setCity] = useState(fieldOrEmpty(personal.city));
  const [houseBuildingNumber, setHouseBuildingNumber] = useState(
    fieldOrEmpty(personal.house),
  );
  const [streetName, setStreetName] = useState(fieldOrEmpty(personal.street));
  const [localityArea, setLocalityArea] = useState(
    fieldOrEmpty(personal.locality),
  );
  const [stateCode, setStateCode] = useState(formDefaults.state || "");
  const [zipCode, setZipCode] = useState(formDefaults.zipCode || "");
  const [comments, setComments] = useState(
    fieldOrEmpty(communication.comments),
  );
  const [panNumber, setPanNumber] = useState(formDefaults.panNumber || "");
  const [dobFull, setDobFull] = useState(formDefaults.dobFull || "");
  const [fatherName, setFatherName] = useState(formDefaults.fatherName || "");
  const [docIncome, setDocIncome] = useState(formDefaults.docIncome || []);
  const [docChq, setDocChq] = useState(formDefaults.docChq || "");
  const [docIdentity, setDocIdentity] = useState(
    formDefaults.docIdentity || [],
  );
  const [addressType, setAddressType] = useState(
    formDefaults.addressType || "",
  );
  const [docAddress, setDocAddress] = useState(formDefaults.docAddress || []);
  const [coappIdentity, setCoappIdentity] = useState(
    formDefaults.coappIdentity || [],
  );
  const [coappPhoto, setCoappPhoto] = useState(formDefaults.coappPhoto || "");
  const [docBusiness, setDocBusiness] = useState(
    formDefaults.docBusiness || [],
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveError, setSaveError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const subStatusOptions = statusSubStatusOptions[status] || [];
  const isAppointmentCreated = status === "Appointment Created";
  const isFollowupRequired = status === "Connected-Followup Required";

  useEffect(() => {
    setCustomerName(fieldOrEmpty(personal.name));
    setProductInterested(formDefaults.productInterested || "");
    setStatus(editableFieldValue(communication.status));
    setSubStatus(editableFieldValue(communication.subStatus));
    setAppointmentDate(formDefaults.appointmentDate || "");
    setFollowupDate(formDefaults.followupDate || "");
    setFosName(editableFieldValue(communication.assignedTo));
    setCity(fieldOrEmpty(personal.city));
    setHouseBuildingNumber(fieldOrEmpty(personal.house));
    setStreetName(fieldOrEmpty(personal.street));
    setLocalityArea(fieldOrEmpty(personal.locality));
    setStateCode(formDefaults.state || "");
    setZipCode(formDefaults.zipCode || "");
    setComments(fieldOrEmpty(communication.comments));
    setPanSelection(formDefaults.docPan || "");
    setPanNumber(formDefaults.panNumber || "");
    setDobFull(formDefaults.dobFull || "");
    setFatherName(formDefaults.fatherName || "");
    setDocIncome(formDefaults.docIncome || []);
    setDocChq(formDefaults.docChq || "");
    setDocIdentity(formDefaults.docIdentity || []);
    setAddressType(formDefaults.addressType || "");
    setDocAddress(formDefaults.docAddress || []);
    setCoappIdentity(formDefaults.coappIdentity || []);
    setCoappPhoto(formDefaults.coappPhoto || "");
    setDocBusiness(formDefaults.docBusiness || []);
    setFieldErrors({});
    setSaveError("");
    setSaveMessage("");
  }, [
    communication.assignedTo,
    communication.comments,
    communication.status,
    communication.subStatus,
    personal.city,
    personal.house,
    personal.locality,
    personal.name,
    personal.street,
    formDefaults.addressType,
    formDefaults.appointmentDate,
    formDefaults.coappIdentity,
    formDefaults.coappPhoto,
    formDefaults.dobFull,
    formDefaults.docAddress,
    formDefaults.docBusiness,
    formDefaults.docChq,
    formDefaults.docIdentity,
    formDefaults.docIncome,
    formDefaults.docPan,
    formDefaults.fatherName,
    formDefaults.followupDate,
    formDefaults.panNumber,
    formDefaults.productInterested,
    formDefaults.state,
    formDefaults.zipCode,
  ]);

  const clearFieldError = useCallback((fieldName) => {
    setFieldErrors((currentErrors) => {
      if (!currentErrors[fieldName]) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      delete nextErrors[fieldName];
      return nextErrors;
    });
  }, []);

  const handleStatusChange = (event) => {
    const nextStatus = event.target.value;
    const nextSubStatusOptions = statusSubStatusOptions[nextStatus] || [];

    setFieldErrors({});
    setStatus(nextStatus);
    setSubStatus((currentSubStatus) =>
      nextSubStatusOptions.includes(currentSubStatus) ? currentSubStatus : "",
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaveMessage("");
    setSaveError("");
    setFieldErrors({});

    const currentUser = getSavedAuthUser() || {};
    const documents = {
      doc_pan: panSelection,
      pan_number: panSelection === "Yes" ? panNumber : "",
      dob_full: inputDateToApiDate(dobFull),
      father_name: fatherName,
      address_type: addressType,
      doc_identity: docIdentity,
      doc_address: docAddress,
      doc_income: docIncome,
      doc_chq: docChq,
      coapp_identity: coappIdentity,
      coapp_photo: coappPhoto,
      doc_business: docBusiness,
    };

    const payload = {
      contact_id: customer.contactId || defaultTelecallingContactId,
      agent_id: currentUser.id || customer.agentId || defaultTelecallingAgentId,
      agent_name: firstBureauValue(
        currentUser.username,
        currentUser.name,
        currentUser.full_name,
        currentUser.email,
        customer.agentName,
      ),
      phone: fieldOrEmpty(personal.phone || customer.searchPhone),
      customer_name: customerName,
      product_interested: productInterested,
      call_status: status,
      sub_status: subStatus,
      fos_name: isAppointmentCreated ? fosName : "",
      appointment_date: isAppointmentCreated ? appointmentDate : "",
      followup_date: isFollowupRequired ? followupDate : "",
      comments,
      city,
      house_building_number: houseBuildingNumber,
      street_name: streetName,
      locality_area: localityArea,
      state: stateCode,
      zip_code: zipCode,
      documents,
    };

    const validationErrors = validateCallEntryPayload(payload);

    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      return;
    }

    setIsSaving(true);

    try {
      const response = await saveTelecallingCallEntry(payload);
      setSaveMessage(callEntrySuccessMessage(response));

      if (response.success && response.saved) {
        onSaved?.({ payload, response });
        window.setTimeout(onClose, 700);
      }
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      aria-hidden={!isVisible}
      className={`fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-10 transition-opacity duration-200 ease-out ${
        isVisible ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
      inert={isVisible ? undefined : ""}
      role="presentation"
    >
      <section
        aria-labelledby="call-entry-modal-title"
        aria-modal="true"
        className={`flex max-h-[calc(100vh-5rem)] w-full max-w-[108rem] origin-top flex-col rounded-[0.5rem] bg-white shadow-[0_16px_48px_rgba(15,23,42,0.28)] transition-all duration-200 ease-out ${
          isVisible
            ? "translate-y-0 scale-100 opacity-100"
            : "translate-y-4 scale-[0.98] opacity-0"
        }`}
        role="dialog"
      >
        <header className="flex items-center justify-between border-b border-[#d9dfe8] px-5 py-4">
          <h2
            className="m-0 flex items-center gap-3 text-[1.75rem] font-bold text-[#111827] max-[720px]:text-2xl"
            id="call-entry-modal-title"
          >
            <FaClipboardList
              className="h-7 w-7 flex-none text-[#c98655]"
              aria-hidden="true"
            />
            Call Entry for {personal.name}
          </h2>
          <button
            aria-label="Close call entry form"
            className="inline-flex h-[38px] w-[38px] items-center justify-center rounded-md border border-transparent text-[#6b7280] transition hover:bg-[#f3f4f6] hover:text-[#111827] focus-visible:bg-[#f3f4f6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9fc8ff]"
            type="button"
            onClick={onClose}
          >
            <FaTimes className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        {(saveMessage || saveError) && (
          <div
            className={`mx-5 mt-4 rounded-md border px-4 py-3 text-sm ${
              saveError
                ? "border-[#f5c2c7] bg-[#f8d7da] text-[#842029]"
                : "border-[#9dd4b6] bg-[#d8efe3] text-[#064e3b]"
            } whitespace-pre-line`}
            role={saveError ? "alert" : "status"}
          >
            {saveError || saveMessage}
          </div>
        )}

        <form
          className="overflow-y-auto py-5 pl-9 pr-8"
          id={callEntryFormId}
          onSubmit={handleSubmit}
        >
          <div className="grid grid-cols-[minmax(24rem,0.9fr)_minmax(28rem,1.35fr)] gap-6 max-[980px]:grid-cols-1">
            <section className="grid gap-4">
              <FormField label="Customer Name" required>
                <input
                  className={modalInputClass}
                  type="text"
                  value={customerName}
                  onChange={(event) => setCustomerName(event.target.value)}
                />
              </FormField>

              <ModalSelectField
                label="Product Interested"
                required
                disabled={isMetadataLoading}
                error={fieldErrors.product_interested}
                options={metadataOptions.productOptions}
                placeholder={
                  isMetadataLoading
                    ? "-- Loading products --"
                    : "-- Select Product --"
                }
                value={productInterested}
                onChange={(event) => {
                  setProductInterested(event.target.value);
                  clearFieldError("product_interested");
                }}
              />

              <ModalSelectField
                label="Status"
                required
                disabled={isMetadataLoading}
                options={metadataOptions.callStatuses}
                placeholder={
                  isMetadataLoading
                    ? "-- Loading statuses --"
                    : "-- Select Status --"
                }
                value={status}
                onChange={handleStatusChange}
              />

              <ModalSelectField
                label="Sub-status"
                required
                disabled={isMetadataLoading || !status}
                options={subStatusOptions}
                placeholder={
                  !status
                    ? "-- Select status first --"
                    : isMetadataLoading
                      ? "-- Loading sub-statuses --"
                      : "-- Select Sub-status --"
                }
                value={subStatus}
                onChange={(event) => setSubStatus(event.target.value)}
              />

              {isAppointmentCreated && (
                <FormField label="Appointment Date & Time">
                  <input
                    className={modalInputClass}
                    step={60}
                    type="datetime-local"
                    value={appointmentDate}
                    onChange={(event) => setAppointmentDate(event.target.value)}
                  />
                </FormField>
              )}

              {isFollowupRequired && (
                <FormField
                  error={fieldErrors.followup_date}
                  label="Follow-up Date & Time"
                  required
                >
                  <input
                    aria-invalid={
                      fieldErrors.followup_date ? "true" : undefined
                    }
                    className={fieldClassName(
                      modalInputClass,
                      fieldErrors.followup_date,
                    )}
                    step={60}
                    type="datetime-local"
                    value={followupDate}
                    onChange={(event) => {
                      setFollowupDate(event.target.value);
                      clearFieldError("followup_date");
                    }}
                  />
                </FormField>
              )}

              {isAppointmentCreated && (
                <ModalSelectField
                  label="FOS Name"
                  required
                  disabled={isMetadataLoading}
                  options={metadataOptions.fosOptions}
                  placeholder={
                    isMetadataLoading
                      ? "-- Loading FOS names --"
                      : "-- Select FOS --"
                  }
                  value={fosName}
                  onChange={(event) => setFosName(event.target.value)}
                />
              )}

              <div className="grid grid-cols-2 gap-4 max-[720px]:grid-cols-1">
                <FormField error={fieldErrors.city} label="City">
                  <input
                    aria-invalid={fieldErrors.city ? "true" : undefined}
                    className={fieldClassName(
                      modalInputClass,
                      fieldErrors.city,
                    )}
                    type="text"
                    value={city}
                    onChange={(event) => {
                      setCity(event.target.value);
                      clearFieldError("city");
                    }}
                  />
                </FormField>
                <FormField
                  error={fieldErrors.house_building_number}
                  label="House / Building No."
                >
                  <input
                    aria-invalid={
                      fieldErrors.house_building_number ? "true" : undefined
                    }
                    className={fieldClassName(
                      modalInputClass,
                      fieldErrors.house_building_number,
                    )}
                    type="text"
                    value={houseBuildingNumber}
                    onChange={(event) => {
                      setHouseBuildingNumber(event.target.value);
                      clearFieldError("house_building_number");
                    }}
                  />
                </FormField>
                <FormField error={fieldErrors.street_name} label="Street Name">
                  <input
                    aria-invalid={fieldErrors.street_name ? "true" : undefined}
                    className={fieldClassName(
                      modalInputClass,
                      fieldErrors.street_name,
                    )}
                    type="text"
                    value={streetName}
                    onChange={(event) => {
                      setStreetName(event.target.value);
                      clearFieldError("street_name");
                    }}
                  />
                </FormField>
                <FormField
                  error={fieldErrors.locality_area}
                  label="Locality / Area"
                >
                  <input
                    aria-invalid={
                      fieldErrors.locality_area ? "true" : undefined
                    }
                    className={fieldClassName(
                      modalInputClass,
                      fieldErrors.locality_area,
                    )}
                    type="text"
                    value={localityArea}
                    onChange={(event) => {
                      setLocalityArea(event.target.value);
                      clearFieldError("locality_area");
                    }}
                  />
                </FormField>
                <ModalSelectField
                  label="State (2-letter)"
                  required
                  disabled={isMetadataLoading}
                  error={fieldErrors.state}
                  options={metadataOptions.stateOptions}
                  placeholder={
                    isMetadataLoading ? "-- Loading states --" : "-- Select --"
                  }
                  value={stateCode}
                  onChange={(event) => {
                    setStateCode(event.target.value);
                    clearFieldError("state");
                  }}
                />
                <FormField error={fieldErrors.zip_code} label="Pin/Zip Code">
                  <input
                    aria-invalid={fieldErrors.zip_code ? "true" : undefined}
                    className={fieldClassName(
                      modalInputClass,
                      fieldErrors.zip_code,
                    )}
                    type="text"
                    value={zipCode}
                    onChange={(event) => {
                      setZipCode(event.target.value);
                      clearFieldError("zip_code");
                    }}
                  />
                </FormField>
              </div>

              <FormField label="Comments">
                <textarea
                  className={modalTextareaClass}
                  value={comments}
                  onChange={(event) => setComments(event.target.value)}
                />
              </FormField>
            </section>

            <section>
              <h3 className="mb-3 flex items-center gap-2 text-[1.55rem] font-bold text-[#111827] max-[720px]:text-xl">
                <FaFileAlt
                  className="h-6 w-6 flex-none text-[#8d819f]"
                  aria-hidden="true"
                />
                Document Checklist
              </h3>

              <h4 className="mb-4 text-base font-bold">
                Group 1: Mandatory (if Appointment Created)
              </h4>

              <div className="grid grid-cols-4 gap-x-7 gap-y-8 max-[1300px]:grid-cols-2 max-[720px]:grid-cols-1">
                <div className="grid gap-3">
                  <OptionGroup error={fieldErrors.doc_pan} label="PAN" required>
                    <RadioOptions
                      isLoading={isMetadataLoading}
                      name="pan"
                      options={getDocumentOptions(metadata, "doc_pan")}
                      selectedValue={panSelection}
                      onChange={(option) => {
                        setPanSelection(option);
                        clearFieldError("doc_pan");
                      }}
                    />
                  </OptionGroup>

                  {panSelection === "Yes" && (
                    <FormField label="PAN Number">
                      <input
                        className={modalInputClass}
                        placeholder="ABCDE1234F"
                        type="text"
                        value={panNumber}
                        onChange={(event) => setPanNumber(event.target.value)}
                      />
                    </FormField>
                  )}
                </div>

                <FormField label="Date of Birth">
                  <input
                    className={modalInputClass}
                    type="date"
                    value={dobFull}
                    onChange={(event) => setDobFull(event.target.value)}
                  />
                </FormField>

                <OptionGroup
                  error={fieldErrors.doc_income}
                  label="Income Proof"
                  required
                >
                  <div className="grid gap-2">
                    <CheckboxOptions
                      isLoading={isMetadataLoading}
                      options={getDocumentOptions(metadata, "doc_income")}
                      selectedValues={docIncome}
                      onChange={(option) => {
                        setDocIncome((currentOptions) =>
                          toggleSelectedOption(currentOptions, option),
                        );
                        clearFieldError("doc_income");
                      }}
                    />
                  </div>
                </OptionGroup>

                <OptionGroup
                  error={fieldErrors.doc_chq}
                  label="CHQ Book"
                  required
                >
                  <RadioOptions
                    isLoading={isMetadataLoading}
                    name="chq-book"
                    options={getDocumentOptions(metadata, "doc_chq")}
                    selectedValue={docChq}
                    onChange={(option) => {
                      setDocChq(option);
                      clearFieldError("doc_chq");
                    }}
                  />
                </OptionGroup>

                <FormField label="Father Name">
                  <input
                    className={modalInputClass}
                    type="text"
                    value={fatherName}
                    onChange={(event) => setFatherName(event.target.value)}
                  />
                </FormField>

                <OptionGroup
                  error={fieldErrors.doc_identity}
                  label="Identity Proof"
                  required
                >
                  <div className="grid gap-2">
                    <CheckboxOptions
                      isLoading={isMetadataLoading}
                      options={getDocumentOptions(metadata, "doc_identity")}
                      selectedValues={docIdentity}
                      onChange={(option) => {
                        setDocIdentity((currentOptions) =>
                          toggleSelectedOption(currentOptions, option),
                        );
                        clearFieldError("doc_identity");
                      }}
                    />
                  </div>
                </OptionGroup>

                <OptionGroup
                  error={fieldErrors.address_type}
                  label="Address Type"
                  required
                >
                  <RadioOptions
                    isLoading={isMetadataLoading}
                    name="address-type"
                    options={getDocumentOptions(metadata, "address_type")}
                    selectedValue={addressType}
                    onChange={(option) => {
                      setAddressType(option);
                      clearFieldError("address_type");
                    }}
                  />
                </OptionGroup>

                <OptionGroup
                  error={fieldErrors.doc_address}
                  label="Address Proof"
                  required
                >
                  <div className="grid gap-2">
                    <CheckboxOptions
                      isLoading={isMetadataLoading}
                      options={getDocumentOptions(metadata, "doc_address")}
                      selectedValues={docAddress}
                      onChange={(option) => {
                        setDocAddress((currentOptions) =>
                          toggleSelectedOption(currentOptions, option),
                        );
                        clearFieldError("doc_address");
                      }}
                    />
                  </div>
                </OptionGroup>
              </div>

              <div className="mt-8 grid gap-6">
                <section>
                  <h4 className="mb-3 text-base font-bold">
                    Group 2: Co-applicant (Optional)
                  </h4>
                  <div className="grid gap-4">
                    <OptionGroup label="Identity Proof">
                      <CheckboxOptions
                        isLoading={isMetadataLoading}
                        options={getDocumentOptions(metadata, "coapp_identity")}
                        selectedValues={coappIdentity}
                        onChange={(option) =>
                          setCoappIdentity((currentOptions) =>
                            toggleSelectedOption(currentOptions, option),
                          )
                        }
                      />
                    </OptionGroup>
                    <OptionGroup label="Photo">
                      <RadioOptions
                        isLoading={isMetadataLoading}
                        name="co-applicant-photo"
                        options={getDocumentOptions(metadata, "coapp_photo")}
                        selectedValue={coappPhoto}
                        onChange={setCoappPhoto}
                      />
                    </OptionGroup>
                  </div>
                </section>

                <section>
                  <h4 className="mb-3 text-base font-bold">
                    Group 3: Business Proof (Optional)
                  </h4>
                  <OptionGroup label="Business Proof">
                    <CheckboxOptions
                      isLoading={isMetadataLoading}
                      options={getDocumentOptions(metadata, "doc_business")}
                      selectedValues={docBusiness}
                      onChange={(option) =>
                        setDocBusiness((currentOptions) =>
                          toggleSelectedOption(currentOptions, option),
                        )
                      }
                    />
                  </OptionGroup>
                </section>
              </div>
            </section>
          </div>
        </form>

        <footer className="flex justify-end gap-3 border-t border-[#d9dfe8] px-5 py-4">
          <button
            className="inline-flex h-[38px] items-center justify-center gap-2 rounded-md bg-[#198754] px-5 text-base font-medium text-white transition hover:bg-[#157347] focus-visible:bg-[#157347] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9dd4b6]"
            disabled={isSaving}
            form={callEntryFormId}
            type="submit"
          >
            <FaSave
              className="h-4 w-4 flex-none text-[#b7a7d8]"
              aria-hidden="true"
            />
            {isSaving ? "Saving..." : "Save"}
          </button>
          <button
            className="inline-flex h-[38px] items-center justify-center rounded-md bg-[#6c757d] px-5 text-base font-medium text-white transition hover:bg-[#5c656d] focus-visible:bg-[#5c656d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#cbd5e1]"
            type="button"
            onClick={onClose}
          >
            Close
          </button>
        </footer>
      </section>
    </div>
  );
}

function TelecallingRecordsPage() {
  const { user } = useAuth();
  const {
    notification,
    isAcknowledging,
    acknowledgeError,
    acknowledge,
  } = useTelecallingNotification(user);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [activePage, setActivePage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isCallEntryMounted, setIsCallEntryMounted] = useState(false);
  const [isCallEntryVisible, setIsCallEntryVisible] = useState(false);
  const [metadata, setMetadata] = useState(emptyTelecallingMetadata);
  const [metadataError, setMetadataError] = useState("");
  const [isMetadataLoading, setIsMetadataLoading] = useState(true);
  const [callSummary, setCallSummary] = useState(emptyCallSummary);
  const [summaryError, setSummaryError] = useState("");
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [callListRecords, setCallListRecords] = useState([]);
  const [callListMeta, setCallListMeta] = useState(emptyCallListMeta);
  const [callListError, setCallListError] = useState("");
  const [isCallListLoading, setIsCallListLoading] = useState(true);
  const [searchError, setSearchError] = useState("");
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const detailsRef = useRef(null);
  const pendingDetailsScrollRef = useRef(false);
  const openFrameRef = useRef(null);

  usePageTitle("Telecalling Records | Pargro");

  const loadCallSummary = useCallback(async () => {
    setSummaryError("");
    setIsSummaryLoading(true);

    try {
      const telecallingSummary = await fetchTelecallingRecordsSummary();
      setCallSummary({
        total_assigned: telecallingSummary.total_assigned,
        total_called: telecallingSummary.total_called,
        total_left: telecallingSummary.total_left,
        total_appointment_taken: telecallingSummary.total_appointment_taken,
      });
    } catch (error) {
      setSummaryError(error.message);
    } finally {
      setIsSummaryLoading(false);
    }
  }, []);

  const loadCallList = useCallback(async () => {
    setCallListError("");
    setIsCallListLoading(true);

    try {
      const response = await fetchTelecallingRecordsList({
        page: activePage,
        status: callListStatusForFilter(activeFilter),
      });

      setCallListRecords((response.rows || []).map(normalizeCallListRecord));
      setCallListMeta({
        total: Number(response.total) || 0,
        page: Number(response.page) || activePage,
        limit: Number(response.limit) || defaultRecordsPerPage,
        totalPages: Number(response.total_pages) || 1,
      });
    } catch (error) {
      setCallListRecords([]);
      setCallListMeta(emptyCallListMeta);
      setCallListError(error.message);
    } finally {
      setIsCallListLoading(false);
    }
  }, [activeFilter, activePage]);

  useEffect(() => {
    let shouldIgnore = false;

    async function loadMetadata() {
      try {
        const telecallingMetadata = await fetchTelecallingRecordsMetadata();

        if (!shouldIgnore) {
          setMetadata(normalizeTelecallingMetadata(telecallingMetadata));
        }
      } catch (error) {
        if (!shouldIgnore) {
          setMetadataError(error.message);
        }
      } finally {
        if (!shouldIgnore) {
          setIsMetadataLoading(false);
        }
      }
    }

    loadMetadata();

    return () => {
      shouldIgnore = true;
    };
  }, []);

  useEffect(() => {
    loadCallSummary();
  }, [loadCallSummary]);

  useEffect(() => {
    loadCallList();
  }, [loadCallList]);

  const revealCustomerDetails = useCallback((customer) => {
    pendingDetailsScrollRef.current = true;
    setSelectedCustomer(customer);
    setHasSearched(true);
  }, []);

  const loadCustomerByPhone = useCallback(
    async (phone, fallbackRecord = {}) => {
      const lookupPhone = String(phone || "").trim();

      if (!lookupPhone) {
        setSearchError("Please enter a phone number to search.");
        return;
      }

      setSearchError("");
      setIsSearchLoading(true);

      const initialBureauPayload = buildTelecallingBureauPayload(
        lookupPhone,
        undefined,
        fallbackRecord,
      );
      const bureauPullRequest = pullCrifBureauReportCallCenter(
        initialBureauPayload,
      )
        .then((bureauData) => ({ bureauData }))
        .catch((bureauError) => ({ bureauError }));

      try {
        const [applicationLookup, telecallingLookup] = await Promise.all([
          fetchApplicationByLeadId(lookupPhone)
            .then((applicationData) => ({ applicationData }))
            .catch((applicationError) => ({ applicationError })),
          searchTelecallingRecord(lookupPhone)
            .then((telecallingData) => ({ telecallingData }))
            .catch((telecallingError) => ({ telecallingError })),
        ]);

        if (
          applicationLookup.applicationError &&
          telecallingLookup.telecallingError
        ) {
          throw applicationLookup.applicationError;
        }

        const searchData = {
          ...(telecallingLookup.telecallingData || {}),
          applicationLookup: applicationLookup.applicationData || null,
          applicationLookupError:
            applicationLookup.applicationError?.message || "",
          telecallingLookupError:
            telecallingLookup.telecallingError?.message || "",
        };
        const customer = mapSearchResponseToCustomer(searchData, lookupPhone);

        setPhoneNumber(customer.searchPhone || lookupPhone);
        revealCustomerDetails(customer);
        setSearchError("");
        setIsSearchLoading(false);

        const bureauResponse = await bureauPullRequest;
        let bureauData = bureauResponse.bureauData;

        if (!bureauData) {
          const enrichedBureauPayload = buildTelecallingBureauPayload(
            lookupPhone,
            searchData,
            fallbackRecord,
          );

          if (
            shouldRetryBureauPull(initialBureauPayload, enrichedBureauPayload)
          ) {
            bureauData = await pullCrifBureauReportCallCenter(
              enrichedBureauPayload,
            ).catch(() => null);
          }
        }

        if (bureauData) {
          const selectedPhone = customer.searchPhone || lookupPhone;

          setSelectedCustomer((currentCustomer) => {
            if (
              !currentCustomer ||
              String(currentCustomer.searchPhone || "") !==
                String(selectedPhone || "")
            ) {
              return currentCustomer;
            }

            return mergeBureauDemographics(currentCustomer, bureauData);
          });
        }
      } catch (error) {
        setSearchError(error.message);
        setIsSearchLoading(false);
      }
    },
    [revealCustomerDetails],
  );

  const openCallEntryModal = useCallback(() => {
    if (openFrameRef.current) {
      cancelAnimationFrame(openFrameRef.current);
    }

    setIsCallEntryMounted(true);
    openFrameRef.current = requestAnimationFrame(() => {
      setIsCallEntryVisible(true);
      openFrameRef.current = null;
    });
  }, []);

  const closeCallEntryModal = useCallback(() => {
    if (openFrameRef.current) {
      cancelAnimationFrame(openFrameRef.current);
      openFrameRef.current = null;
    }

    setIsCallEntryVisible(false);
  }, []);

  useEffect(() => {
    return () => {
      if (openFrameRef.current) {
        cancelAnimationFrame(openFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isCallEntryVisible) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeCallEntryModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeCallEntryModal, isCallEntryVisible]);

  useEffect(() => {
    if (!hasSearched || !selectedCustomer || !pendingDetailsScrollRef.current) {
      return;
    }

    pendingDetailsScrollRef.current = false;
    detailsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hasSearched, selectedCustomer]);

  const handleSearch = (event) => {
    event.preventDefault();
    const lookupPhone = String(phoneNumber || "").trim();
    const fallbackRecord = callListRecords.find(
      (record) => String(record.phone || "") === lookupPhone,
    );

    loadCustomerByPhone(phoneNumber, fallbackRecord);
  };

  const handleActionClick = (record) => {
    setPhoneNumber(record.phone);
    loadCustomerByPhone(record.phone, record);
  };

  const handleCallEntrySaved = useCallback(
    async ({ payload, response }) => {
      const savedPhone = String(payload.phone || "").trim();
      const newStatus = payload.call_status || "";
      const nextAction = callListActionForStatus(newStatus);
      const savedDocuments = payload.documents || {};
      const savedResponseData = response?.data || response || {};
      const pilResponseData = response?.downstream?.pil?.data?.data || {};
      const pilMemberProfile = pilResponseData.memberProfile || {};
      const remainsInActiveFilter = callStatusMatchesFilter(
        newStatus,
        activeFilter,
      );
      const hasVisibleRecord = callListRecords.some(
        (record) => String(record.phone || "").trim() === savedPhone,
      );
      const removedVisibleRecord =
        hasVisibleRecord && !remainsInActiveFilter;

      setSelectedCustomer((currentCustomer) => {
        if (
          !currentCustomer ||
          String(
            currentCustomer.searchPhone ||
              currentCustomer.personal?.phone ||
              "",
          ).trim() !==
            savedPhone
        ) {
          return currentCustomer;
        }

        return {
          ...currentCustomer,
          agentId: payload.agent_id || currentCustomer.agentId,
          agentName: payload.agent_name || currentCustomer.agentName,
          formDefaults: {
            ...(currentCustomer.formDefaults || {}),
            productInterested: payload.product_interested || "",
            appointmentDate: payload.appointment_date || "",
            followupDate: payload.followup_date || "",
            state: payload.state || "",
            zipCode: payload.zip_code || "",
            docPan: savedDocuments.doc_pan || "",
            panNumber: savedDocuments.pan_number || "",
            dobFull: apiDateToInputDate(savedDocuments.dob_full),
            fatherName: savedDocuments.father_name || "",
            addressType: savedDocuments.address_type || "",
            docIdentity: normalizeSelectionArray(savedDocuments.doc_identity),
            docAddress: normalizeSelectionArray(savedDocuments.doc_address),
            docIncome: normalizeSelectionArray(savedDocuments.doc_income),
            docChq: savedDocuments.doc_chq || "",
            coappIdentity: normalizeSelectionArray(
              savedDocuments.coapp_identity,
            ),
            coappPhoto: savedDocuments.coapp_photo || "",
            docBusiness: normalizeSelectionArray(savedDocuments.doc_business),
          },
          personal: {
            ...currentCustomer.personal,
            name: displayValue(payload.customer_name),
            phone: displayValue(savedPhone),
            city: displayValue(payload.city),
            house: displayValue(payload.house_building_number),
            street: displayValue(payload.street_name),
            locality: displayValue(payload.locality_area),
          },
          documents: {
            ...currentCustomer.documents,
            panPresent: displayValue(savedDocuments.doc_pan, "N/A"),
            panNumber: displayValue(savedDocuments.pan_number, "N/A"),
            idProof: displayValue(
              normalizeSelectionArray(savedDocuments.doc_identity),
              "N/A",
            ),
            dob: displayValue(savedDocuments.dob_full, "N/A"),
          },
          pil: {
            ...currentCustomer.pil,
            membershipNo: displayValue(
              pilMemberProfile.membershipNumber,
              currentCustomer.pil?.membershipNo || "PIL Info Not Fetched Yet",
            ),
            memberSince: displayValue(
              pilMemberProfile.memberSince,
              currentCustomer.pil?.memberSince || "",
            ),
            classification: displayValue(
              pilMemberProfile.classification,
              currentCustomer.pil?.classification || "",
            ),
          },
          communication: {
            ...currentCustomer.communication,
            lastCalled: displayValue(
              savedResponseData.last_call_date,
              currentLocalDateTime(),
            ),
            status: displayValue(newStatus, "N/A"),
            subStatus: displayValue(payload.sub_status, "N/A"),
            assignedTo: displayValue(payload.fos_name, ""),
            comments: displayValue(payload.comments, ""),
          },
          application: {
            ...currentCustomer.application,
            loanProduct: displayValue(payload.product_interested, "N/A"),
          },
        };
      });

      setCallListRecords((currentRecords) =>
        currentRecords.flatMap((record) => {
          if (String(record.phone || "").trim() !== savedPhone) {
            return [record];
          }

          if (!remainsInActiveFilter) {
            return [];
          }

          return [
            {
              ...record,
              phone: savedPhone || record.phone,
              name: payload.customer_name || "-",
              city: payload.city || "-",
              status: newStatus,
              subStatus: payload.sub_status || "",
              action: nextAction.action,
              actionClass: nextAction.actionClass,
              raw: {
                ...record.raw,
                phone: savedPhone || record.phone,
                name: payload.customer_name || "-",
                city: payload.city || "-",
                call_status: newStatus,
                sub_status: payload.sub_status || "",
                action: {
                  ...record.raw?.action,
                  phone: savedPhone || record.phone,
                  button_text: nextAction.action,
                  button_class: nextAction.actionClass,
                },
              },
            },
          ];
        }),
      );

      if (removedVisibleRecord) {
        setCallListMeta((currentMeta) => {
          const nextTotal = Math.max(0, currentMeta.total - 1);
          const calculatedTotalPages = Math.max(
            1,
            Math.ceil(nextTotal / Math.max(1, currentMeta.limit)),
          );

          return {
            ...currentMeta,
            total: nextTotal,
            totalPages: Math.max(activePage, calculatedTotalPages),
          };
        });
      }

      await loadCallSummary();
    },
    [activeFilter, activePage, callListRecords, loadCallSummary],
  );

  const summaryDisplayItems = summaryFields.map(([label, field]) => [
    label,
    summaryCount(callSummary[field], isSummaryLoading),
  ]);
  const totalPages = Math.max(1, callListMeta.totalPages);
  const paginationPages = Array.from(
    { length: totalPages },
    (_, index) => index + 1,
  );
  const shouldShowPagination = totalPages > 1;

  return (
    <ActionPageLayout
      title="Telecalling Customer Lookup"
      icon={
        <FaPhoneAlt
          className="h-6 w-6 flex-none rotate-[-18deg] text-[#d63384]"
          aria-hidden="true"
        />
      }
    >
      <TelecallingNotificationAlert
        acknowledgeError={acknowledgeError}
        isAcknowledging={isAcknowledging}
        notification={notification}
        onAcknowledge={acknowledge}
      />

      {metadataError && (
        <div
          className="mt-6 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-4 py-3 text-sm text-[#842029]"
          role="alert"
        >
          {metadataError}
        </div>
      )}

      <section className="mt-6">
        <h2 className="mb-3 flex items-center gap-3 text-[1.75rem] font-bold text-[#171717] max-[720px]:text-2xl">
          <FaSearch
            className="h-8 w-8 flex-none text-[#6d4b8a]"
            aria-hidden="true"
          />
          Telecalling Customer Lookup
        </h2>

        <div className="grid grid-cols-[minmax(20rem,1fr)_minmax(24rem,1fr)] items-start gap-4 max-[980px]:grid-cols-1">
          <form
            className="grid grid-cols-[minmax(16rem,1fr)_minmax(10rem,auto)] items-end gap-4 self-start rounded-[0.5rem] border border-[#d9dfe8] bg-[#f8fafc] p-4 shadow-[0_2px_8px_rgba(15,23,42,0.12)] max-[720px]:grid-cols-1"
            onSubmit={handleSearch}
          >
            <label className="block" htmlFor="telecalling-phone">
              <span className="mb-1.5 block text-base">Phone Number</span>
              <input
                className={inputClass}
                id="telecalling-phone"
                inputMode="numeric"
                maxLength={10}
                placeholder="Enter phone number"
                type="tel"
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
              />
            </label>

            <button
              className="inline-flex h-[38px] items-center justify-center gap-2 rounded-md bg-[#1773f8] px-6 text-base font-medium text-white shadow transition hover:bg-[#0d63dc] focus-visible:bg-[#0d63dc] disabled:cursor-not-allowed disabled:bg-[#8bbcfb]"
              disabled={isSearchLoading}
              type="submit"
            >
              <FaSearch className="h-4 w-4 flex-none" aria-hidden="true" />
              {isSearchLoading ? "Searching..." : "Search"}
            </button>

            {searchError && (
              <p
                className="col-span-2 m-0 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-3 py-2 text-sm text-[#842029] max-[720px]:col-span-1"
                role="alert"
              >
                {searchError}
              </p>
            )}
          </form>

          <section className="rounded-[0.5rem] border border-[#d1d5db] bg-white p-4 shadow-[0_2px_8px_rgba(15,23,42,0.12)]">
            <h3 className="mb-5 flex items-center gap-2 text-base font-semibold text-[#111827]">
              <FaChartBar
                className="h-4 w-4 flex-none text-[#0d73ff]"
                aria-hidden="true"
              />
              Call Summary for Today
            </h3>

            {summaryError && (
              <p
                className="mb-4 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-3 py-2 text-sm text-[#842029]"
                role="alert"
              >
                {summaryError}
              </p>
            )}

            <dl className="grid grid-cols-2 gap-x-7 gap-y-3 text-base leading-[1.45] max-[720px]:grid-cols-1">
              {summaryDisplayItems.map(([label, value]) => (
                <div className="flex min-w-0 items-baseline gap-2" key={label}>
                  <dt className="font-bold">{label}:</dt>
                  <dd className="m-0">{value}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>
      </section>

      {hasSearched && selectedCustomer && (
        <div
          className="scroll-mt-24"
          id="telecalling-customer-details"
          ref={detailsRef}
        >
          <SearchResultDetails
            customer={selectedCustomer}
            onOpenCallEntry={openCallEntryModal}
          />
        </div>
      )}

      <section className="mt-6">
        <h2 className="mb-5 flex items-center gap-3 text-[1.75rem] font-bold text-[#171717] max-[720px]:text-2xl">
          <FaClipboardList
            className="h-8 w-8 flex-none text-[#c98655]"
            aria-hidden="true"
          />
          Today's Call List
        </h2>

        <div className="flex flex-wrap gap-3">
          {filterButtons.map((filter) => {
            const isActive = activeFilter === filter.id;

            return (
              <button
                className={`inline-flex h-[38px] items-center justify-center rounded-md border px-5 text-base font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9fc8ff] ${filter.className} ${
                  isActive ? "shadow-[0_0_0_3px_rgba(13,115,255,0.18)]" : ""
                }`}
                key={filter.id}
                type="button"
                onClick={() => {
                  setActiveFilter(filter.id);
                  setActivePage(1);
                }}
              >
                {filter.label}
              </button>
            );
          })}
        </div>

        <div className="mt-6 overflow-x-auto border border-[#d9dfe8] bg-white shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
          <table className="w-full min-w-[920px] border-collapse text-left">
            <thead>
              <tr>
                {tableHeaders.map((header) => (
                  <th
                    className="border-b border-r border-[#d9dfe8] bg-[#f8f9fa] px-3 py-3 text-base font-bold text-black last:border-r-0"
                    key={header}
                    scope="col"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isCallListLoading && (
                <tr>
                  <td
                    className="border-t border-[#d9dfe8] px-3 py-5 text-center text-base text-[#6b7280]"
                    colSpan={tableHeaders.length}
                  >
                    Loading call list...
                  </td>
                </tr>
              )}

              {!isCallListLoading && callListError && (
                <tr>
                  <td
                    className="border-t border-[#d9dfe8] px-3 py-5 text-center text-base text-[#842029]"
                    colSpan={tableHeaders.length}
                  >
                    {callListError}
                  </td>
                </tr>
              )}

              {!isCallListLoading &&
                !callListError &&
                callListRecords.length === 0 && (
                  <tr>
                    <td
                      className="border-t border-[#d9dfe8] px-3 py-5 text-center text-base text-[#6b7280]"
                      colSpan={tableHeaders.length}
                    >
                      No call records found.
                    </td>
                  </tr>
                )}

              {!isCallListLoading &&
                !callListError &&
                callListRecords.map((record, index) => {
                  const isSelected =
                    selectedCustomer?.searchPhone === record.phone;

                  return (
                    <tr
                      className={index % 2 === 0 ? "bg-[#f1f1f1]" : "bg-white"}
                      key={`${record.phone}-${activePage}-${index}`}
                    >
                      <td className="border-r border-t border-[#d9dfe8] px-3 py-4 text-base">
                        {record.phone}
                      </td>
                      <td className="border-r border-t border-[#d9dfe8] px-3 py-4 text-base">
                        {record.name}
                      </td>
                      <td className="border-r border-t border-[#d9dfe8] px-3 py-4 text-base">
                        {record.city}
                      </td>
                      <td className="border-r border-t border-[#d9dfe8] px-3 py-4 text-base">
                        {record.status}
                      </td>
                      <td className="border-r border-t border-[#d9dfe8] px-3 py-4 text-base">
                        {record.subStatus}
                      </td>
                      <td className="border-t border-[#d9dfe8] px-3 py-3">
                        <button
                          aria-controls="telecalling-customer-details"
                          aria-expanded={isSelected ? "true" : "false"}
                          className={`inline-flex h-[38px] items-center justify-center rounded-md px-5 text-base font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9fc8ff] disabled:cursor-not-allowed disabled:opacity-70 ${actionButtonClass(record.action, record.actionClass)}`}
                          disabled={isSearchLoading}
                          type="button"
                          onClick={() => handleActionClick(record)}
                        >
                          {record.action}
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {shouldShowPagination && (
          <nav
            aria-label="Telecalling records pagination"
            className="mt-6 flex flex-wrap"
          >
            {paginationPages.map((page) => {
              const isActivePage = activePage === page;

              return (
                <button
                  aria-current={isActivePage ? "page" : undefined}
                  className={`h-[38px] min-w-[48px] border border-r-0 border-[#d9dfe8] px-3 text-base font-medium transition first:rounded-l-md last:rounded-r-md last:border-r focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9fc8ff] ${
                    isActivePage
                      ? "bg-[#1773f8] text-white"
                      : "bg-white text-[#0d73ff] hover:bg-[#eff6ff]"
                  }`}
                  key={page}
                  type="button"
                  onClick={() => setActivePage(page)}
                >
                  {page}
                </button>
              );
            })}
          </nav>
        )}
      </section>

      {isCallEntryMounted && (
        <CallEntryModal
          customer={selectedCustomer}
          isMetadataLoading={isMetadataLoading}
          isVisible={isCallEntryVisible}
          metadata={metadata}
          onClose={closeCallEntryModal}
          onSaved={handleCallEntrySaved}
        />
      )}
    </ActionPageLayout>
  );
}

export default TelecallingRecordsPage;
